import React, { useState, useEffect, useCallback, useMemo } from 'react';
import throttle from 'lodash/throttle';

/**
 * A hook that provides the current viewport dimensions.
 * Returns an object with the shape `{ width: 600, height: 400}`.
 */
export const useViewport = () => {
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  const updateViewport = useCallback(() => {
    setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  // Throttle the resize handler to avoid excessive re-renders
  const throttledUpdateViewport = useMemo(() => throttle(updateViewport, 100), [updateViewport]);

  useEffect(() => {
    // Set initial viewport
    updateViewport();

    window.addEventListener('resize', throttledUpdateViewport);
    window.addEventListener('orientationchange', throttledUpdateViewport);

    return () => {
      window.removeEventListener('resize', throttledUpdateViewport);
      window.removeEventListener('orientationchange', throttledUpdateViewport);
      throttledUpdateViewport.cancel();
    };
  }, [updateViewport, throttledUpdateViewport]);

  return viewport;
};

/**
 * A higher order component (HOC) that provides the current viewport
 * dimensions to the wrapped component as a `viewport` prop that has
 * the shape `{ width: 600, height: 400}`.
 */
export const withViewport = Component => {
  const WithViewportComponent = props => {
    const viewport = useViewport();
    return <Component {...props} viewport={viewport} />;
  };

  WithViewportComponent.displayName = `withViewport(${Component.displayName || Component.name})`;

  return WithViewportComponent;
};

/**
 * A hook that provides dimensions for a given element reference.
 * Returns an object with the shape `{ width: 600, height: 400}`.
 */
export const useDimensions = elementRef => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const updateDimensions = useCallback(() => {
    if (elementRef.current) {
      const { clientWidth, clientHeight } = elementRef.current;
      setDimensions({ width: clientWidth, height: clientHeight });
    }
  }, [elementRef]);

  // Throttle the resize handler to avoid excessive re-renders
  const throttledUpdateDimensions = useMemo(() => throttle(updateDimensions, 200), [
    updateDimensions,
  ]);

  useEffect(() => {
    // Set initial dimensions after a small delay for styled paint
    const initialTimeout = setTimeout(updateDimensions, 100);

    window.addEventListener('resize', throttledUpdateDimensions);
    window.addEventListener('orientationchange', throttledUpdateDimensions);

    return () => {
      clearTimeout(initialTimeout);
      window.removeEventListener('resize', throttledUpdateDimensions);
      window.removeEventListener('orientationchange', throttledUpdateDimensions);
      throttledUpdateDimensions.cancel();
    };
  }, [updateDimensions, throttledUpdateDimensions]);

  // Handle resize with requestAnimationFrame for better performance
  const handleResize = useCallback(() => {
    window.requestAnimationFrame(updateDimensions);
  }, [updateDimensions]);

  useEffect(() => {
    const throttledHandleResize = throttle(handleResize, 200);

    window.addEventListener('resize', throttledHandleResize);
    window.addEventListener('orientationchange', throttledHandleResize);

    return () => {
      window.removeEventListener('resize', throttledHandleResize);
      window.removeEventListener('orientationchange', throttledHandleResize);
      throttledHandleResize.cancel();
    };
  }, [handleResize]);

  return dimensions;
};

/**
 * A higher order component (HOC) that provides dimensions to the wrapped component as a
 * `dimensions` prop that has the shape `{ width: 600, height: 400}`.
 *
 * @param {React.Component} Component to be wrapped by this HOC
 * @param {Object} options pass in options like maxWidth and maxHeight.
 *
 * @return {Object} HOC component which knows its dimensions
 */
export const withDimensions = (Component, options = {}) => {
  const WithDimensionsComponent = props => {
    const [elementRef, setElementRef] = useState(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    const updateDimensions = useCallback(() => {
      if (elementRef) {
        const { clientWidth, clientHeight } = elementRef;
        setDimensions({ width: clientWidth, height: clientHeight });
      }
    }, [elementRef]);

    useEffect(() => {
      // Set initial dimensions after a small delay for styled paint
      const initialTimeout = setTimeout(updateDimensions, 100);

      const handleResize = () => {
        window.requestAnimationFrame(updateDimensions);
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('orientationchange', handleResize);

      return () => {
        clearTimeout(initialTimeout);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
      };
    }, [updateDimensions]);

    // Dimensions from state (i.e. dimension after previous resize)
    // These are needed for component rerenders
    const { width, height } = dimensions;

    // Current dimensions from element reference
    const { clientWidth, clientHeight } = elementRef || { clientWidth: 0, clientHeight: 0 };
    const hasDimensions =
      (width !== 0 && height !== 0) || (clientWidth !== 0 && clientHeight !== 0);

    // clientWidth and clientHeight
    const currentDimensions =
      clientWidth !== 0 && clientHeight !== 0
        ? { width: clientWidth, height: clientHeight }
        : width !== 0 && height !== 0
        ? { width, height }
        : {};

    // lazyLoadWithDimensions HOC needs to take all given space
    // unless max dimensions are provided through options.
    const { maxWidth, maxHeight } = options;
    const maxWidthMaybe = maxWidth ? { maxWidth } : {};
    const maxHeightMaybe = maxHeight ? { maxHeight } : {};
    const style =
      maxWidth || maxHeight
        ? { width: '100%', height: '100%', ...maxWidthMaybe, ...maxHeightMaybe }
        : { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 };

    return (
      <div
        ref={element => {
          setElementRef(element);
        }}
        style={style}
      >
        {hasDimensions ? <Component {...props} dimensions={currentDimensions} /> : null}
      </div>
    );
  };

  WithDimensionsComponent.displayName = `withDimensions(${Component.displayName ||
    Component.name})`;

  return WithDimensionsComponent;
};

/**
 * A hook that provides lazy-loaded dimensions for a given element reference.
 * The element is only measured when it's near the viewport.
 * Returns an object with the shape `{ width: 600, height: 400}`.
 */
export const useLazyDimensions = (elementRef, options = {}) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const isElementNearViewport = useCallback(
    (safetyBoundary = 0) => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        return (
          (rect.top >= 0 && rect.top <= viewportHeight + safetyBoundary) ||
          (rect.bottom >= -1 * safetyBoundary && rect.bottom <= viewportHeight)
        );
      }
      return false;
    },
    [elementRef]
  );

  const updateDimensions = useCallback(() => {
    if (elementRef.current) {
      const { clientWidth, clientHeight } = elementRef.current;
      setDimensions({ width: clientWidth, height: clientHeight });
    }
  }, [elementRef]);

  useEffect(() => {
    const RENDER_WAIT_MS = 100;
    const NEAR_VIEWPORT_MARGIN = 50;

    const initialTimeout = setTimeout(() => {
      if (isElementNearViewport(0)) {
        updateDimensions();
      } else {
        const loadAfterInitialRendering = options.loadAfterInitialRendering;
        if (typeof loadAfterInitialRendering === 'number') {
          const afterRenderTimeout = setTimeout(() => {
            window.requestAnimationFrame(updateDimensions);
          }, loadAfterInitialRendering);

          return () => {
            clearTimeout(afterRenderTimeout);
          };
        }
      }
    }, RENDER_WAIT_MS);

    const handleScroll = () => {
      const shouldLoadToImproveScrolling = typeof options.loadAfterInitialRendering === 'number';
      if (isElementNearViewport(NEAR_VIEWPORT_MARGIN) || shouldLoadToImproveScrolling) {
        window.requestAnimationFrame(updateDimensions);
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 200);

    window.addEventListener('scroll', throttledHandleScroll);
    window.addEventListener('resize', throttledHandleScroll);
    window.addEventListener('orientationchange', throttledHandleScroll);

    return () => {
      clearTimeout(initialTimeout);
      window.removeEventListener('scroll', throttledHandleScroll);
      window.removeEventListener('resize', throttledHandleScroll);
      window.removeEventListener('orientationchange', throttledHandleScroll);
      throttledHandleScroll.cancel();
    };
  }, [isElementNearViewport, updateDimensions, options.loadAfterInitialRendering]);

  return dimensions;
};

/**
 * A higher order component (HOC) that lazy loads the current element and provides
 * dimensions to the wrapped component as a `dimensions` prop that has
 * the shape `{ width: 600, height: 400}`.
 *
 * @param {React.Component} Component to be wrapped by this HOC
 * @param {Object} options pass in options like maxWidth and maxHeight. To load component after
 * initial rendering has passed or after user has interacted with the window (e.g. scrolled),
 * use`loadAfterInitialRendering: 1500` (value should be milliseconds).
 *
 * @return {Object} HOC component which knows its dimensions
 */
export const lazyLoadWithDimensions = (Component, options = {}) => {
  const LazyLoadWithDimensionsComponent = props => {
    const [elementRef, setElementRef] = useState(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    const isElementNearViewport = useCallback(
      (safetyBoundary = 0) => {
        if (elementRef) {
          const rect = elementRef.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

          return (
            (rect.top >= 0 && rect.top <= viewportHeight + safetyBoundary) ||
            (rect.bottom >= -1 * safetyBoundary && rect.bottom <= viewportHeight)
          );
        }
        return false;
      },
      [elementRef]
    );

    const updateDimensions = useCallback(() => {
      if (elementRef) {
        const { clientWidth, clientHeight } = elementRef;
        setDimensions({ width: clientWidth, height: clientHeight });
      }
    }, [elementRef]);

    useEffect(() => {
      const RENDER_WAIT_MS = 100;
      const NEAR_VIEWPORT_MARGIN = 50;

      const initialTimeout = setTimeout(() => {
        if (isElementNearViewport(0)) {
          updateDimensions();
        } else {
          const loadAfterInitialRendering = options.loadAfterInitialRendering;
          if (typeof loadAfterInitialRendering === 'number') {
            const afterRenderTimeout = setTimeout(() => {
              window.requestAnimationFrame(updateDimensions);
            }, loadAfterInitialRendering);

            return () => {
              clearTimeout(afterRenderTimeout);
            };
          }
        }
      }, RENDER_WAIT_MS);

      const handleScroll = () => {
        const shouldLoadToImproveScrolling = typeof options.loadAfterInitialRendering === 'number';
        if (isElementNearViewport(NEAR_VIEWPORT_MARGIN) || shouldLoadToImproveScrolling) {
          window.requestAnimationFrame(updateDimensions);
        }
      };

      const throttledHandleScroll = throttle(handleScroll, 200);

      window.addEventListener('scroll', throttledHandleScroll);
      window.addEventListener('resize', throttledHandleScroll);
      window.addEventListener('orientationchange', throttledHandleScroll);

      return () => {
        clearTimeout(initialTimeout);
        window.removeEventListener('scroll', throttledHandleScroll);
        window.removeEventListener('resize', throttledHandleScroll);
        window.removeEventListener('orientationchange', throttledHandleScroll);
        throttledHandleScroll.cancel();
      };
    }, [isElementNearViewport, updateDimensions, options.loadAfterInitialRendering]);

    const { width, height } = dimensions;

    // lazyLoadWithDimensions HOC needs to take all given space
    // unless max dimensions are provided through options.
    const { maxWidth, maxHeight } = options;
    const maxWidthMaybe = maxWidth ? { maxWidth } : {};
    const maxHeightMaybe = maxHeight ? { maxHeight } : {};
    const style =
      maxWidth || maxHeight
        ? { width: '100%', height: '100%', ...maxWidthMaybe, ...maxHeightMaybe }
        : { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 };

    return (
      <div
        ref={element => {
          setElementRef(element);
        }}
        style={style}
      >
        {width !== 0 && height !== 0 ? <Component {...props} dimensions={dimensions} /> : null}
      </div>
    );
  };

  LazyLoadWithDimensionsComponent.displayName = `lazyLoadWithDimensions(${Component.displayName ||
    Component.name})`;

  return LazyLoadWithDimensionsComponent;
};
